import { Effect, Layer, Schema } from "effect";
import { HttpClient } from "effect/unstable/http";
import { HttpApi, HttpApiClient } from "effect/unstable/httpapi";
import { KvKey, KvListInput, MAX_KV_VALUE_BYTES, ResourceApi, ResourcePublicFailureBody, ResourceWorkspaceId } from "@workerstation/shared/resources/http";
import { ResourceRequestError, resourceInvalid, resourceUnavailable } from "@workerstation/shared/errors/resource";
import type { ResourceFailure } from "@workerstation/shared/errors/resource";
import { PreparedKVTarget } from "./model.ts";
import type { KVListOptions, KVTarget } from "./model.ts";
import { KVApi } from "./service.ts";

const Api = HttpApi.make("FrontendKVApi").add(ResourceApi);
const Envelope = Schema.Struct({ error: Schema.optionalKey(Schema.Json) });
const own = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.scoped(operation.pipe(Effect.mapError((error): ResourceFailure => Schema.is(ResourceRequestError)(error) ? error : resourceUnavailable())));
const workspace = (value: string) => Schema.decodeUnknownEffect(ResourceWorkspaceId)(value).pipe(Effect.mapError(() => resourceInvalid("Workerspace id must be a lowercase slug.")));
const target = (value: KVTarget) => Schema.decodeUnknownEffect(PreparedKVTarget)(value).pipe(Effect.mapError(() => resourceInvalid("Invalid KV target.")));
const key = (value: string) => Schema.decodeUnknownEffect(KvKey)(value).pipe(Effect.mapError(() => resourceInvalid("KV keys must contain between 1 and 512 UTF-8 bytes.")));
const publicRefusal = (response: import("effect/unstable/http/HttpClientResponse").HttpClientResponse) => {
  const status = response.status;
  if (status !== 400 && status !== 404 && status !== 409) return Effect.fail(resourceUnavailable());
  return response.json.pipe(
    Effect.flatMap(Schema.decodeUnknownEffect(ResourcePublicFailureBody)),
    Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(body => Effect.fail(new ResourceRequestError({ status, message: body.error, ...(body.available === undefined ? {} : { available: body.available }) })))
  );
};

/** The caller owns queries or accepted mutations; each operation owns its response. */
export const makeKVApi = Effect.fn("Frontend.KVApi.make")(function* (baseUrl: string) {
  const http = HttpClient.withScope(yield* HttpClient.HttpClient);
  const jsonHttp = HttpClient.transformResponse(http, effect => effect.pipe(
    Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(response => response.status >= 200 && response.status < 300 ? response.json.pipe(
      Effect.flatMap(Schema.decodeUnknownEffect(Envelope)),
      Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(body => body.error ? Effect.fail(resourceUnavailable()) : Effect.succeed(response))
    ) : publicRefusal(response))
  ));
  const client = yield* HttpApiClient.makeWith(Api, { httpClient: jsonHttp, baseUrl });
  const textClient = yield* HttpApiClient.makeWith(Api, { httpClient: http, baseUrl });
  return KVApi.of({
    bindings: Effect.fn("Frontend.KVApi.bindings")(function* (workspaceId) {
      const selected = yield* workspace(workspaceId);
      const response = yield* client.resources.kvBindings({ params: { workspaceId: selected } });
      if (response.workspaceId !== selected) return yield* Effect.fail(resourceUnavailable());
      return response.bindings;
    }, own),
    entries: Effect.fn("Frontend.KVApi.entries")(function* (selected, options: KVListOptions = {}) {
      const prepared = yield* target(selected);
      const page = yield* Schema.decodeUnknownEffect(KvListInput)({ ...options, limit: options.limit || 1000 }).pipe(Effect.mapError(() => resourceInvalid("Invalid KV page options.")));
      const result = yield* client.resources.kvKeys({ params: prepared, query: { namespace: prepared.namespace, include: "values", ...(options.prefix ? { prefix: page.prefix } : {}), ...(options.cursor ? { cursor: page.cursor } : {}), ...(options.limit ? { limit: String(page.limit) } : {}) } });
      return { entries: result.keys.map(entry => ({ name: entry.name, ...(entry.expiration === undefined ? {} : { expiration: entry.expiration }), value: entry.value ?? null })), cursor: result.cursor ?? null };
    }, own),
    read: Effect.fn("Frontend.KVApi.read")(function* (selected, selectedKey) {
      const prepared = yield* target(selected);
      const response = yield* textClient.resources.kvValue({ params: prepared, query: { namespace: prepared.namespace, key: yield* key(selectedKey) }, responseMode: "response-only" });
      if (response.status === 404) return null;
      if (response.status < 200 || response.status >= 300) return yield* publicRefusal(response);
      return yield* response.text;
    }, own),
    write: Effect.fn("Frontend.KVApi.write")(function* (selected, selectedKey, value) {
      const prepared = yield* target(selected), selectedKeyValue = yield* key(selectedKey);
      const text = yield* Schema.decodeUnknownEffect(Schema.String)(value).pipe(Effect.mapError(() => resourceInvalid("KV values must be text.")));
      if (new TextEncoder().encode(text).byteLength > MAX_KV_VALUE_BYTES) return yield* Effect.fail(resourceInvalid("KV values must be 25 MiB or less."));
      yield* client.resources.kvPut({ params: prepared, query: { namespace: prepared.namespace, key: selectedKeyValue }, payload: text });
    }, own),
    delete: Effect.fn("Frontend.KVApi.delete")(function* (selected, selectedKey) {
      const prepared = yield* target(selected);
      yield* client.resources.kvDelete({ params: prepared, query: { namespace: prepared.namespace, key: yield* key(selectedKey) } });
    }, own)
  });
});
export const KVApiLive = (baseUrl: string) => Layer.effect(KVApi, makeKVApi(baseUrl));
